'use client'

import { CollectionContainer } from '@/components/common/collection/collection-container'
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import { useIsMobileClient } from '@/hooks/common/use-device-type'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn } from '@/lib/utils'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useEffect, useState } from 'react'

interface OutfitHighlight {
  image: string
  title: string
  description: string
}

export const SeamlessOutfitHighlights = () => {
  const t = useTranslations()
  const isMobile = useIsMobileClient()
  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null)

  const outfitHighlights: OutfitHighlight[] = [
    {
      image: '/image/June2025/seamless-1.jpg',
      title: t('seamless_hardcode.outfit_highlights.seamless_comfort.title'),
      description: t(
        'seamless_hardcode.outfit_highlights.seamless_comfort.description'
      ),
    },
    {
      image: '/image/June2025/seamless-2.jpg',
      title: t('seamless_hardcode.outfit_highlights.four_way_stretch.title'),
      description: t(
        'seamless_hardcode.outfit_highlights.four_way_stretch.description'
      ),
    },
    {
      image: '/image/June2025/seamless-3.jpg',
      title: t('seamless_hardcode.outfit_highlights.quick_dry.title'),
      description: t(
        'seamless_hardcode.outfit_highlights.quick_dry.description'
      ),
    },
    {
      image: '/image/June2025/seamless-4.jpg',
      title: t('seamless_hardcode.outfit_highlights.minimalist_design.title'),
      description: t(
        'seamless_hardcode.outfit_highlights.minimalist_design.description'
      ),
    },
  ]

  // Auto play effect
  useEffect(() => {
    if (!carouselApi || outfitHighlights.length < 2) return
    const interval = setInterval(() => {
      if (carouselApi) {
        carouselApi.scrollNext()
      }
    }, 3000) // 3 seconds
    return () => clearInterval(interval)
  }, [carouselApi, outfitHighlights.length])

  return (
    <CollectionContainer>
      <Carousel
        opts={{
          align: 'start',
          loop: true,
          skipSnaps: false,
          slidesToScroll: 1,
        }}
        setApi={setCarouselApi}
        className='w-full'
      >
        <CarouselContent className='-ml-4'>
          {outfitHighlights.map((outfitHighlight, index) => (
            <CarouselItem
              key={index}
              className={cn(
                'group cursor-pointer pl-4',
                isMobile ? 'basis-[66.666%]' : 'basis-1/4'
              )}
            >
              <div className='relative overflow-hidden rounded-lg'>
                <div className='aspect-[434/581]'>
                  <Image
                    src={ImageHelper.getImageUrlCdn(outfitHighlight.image)}
                    alt={outfitHighlight.title}
                    fill
                    className='h-full w-full object-cover transition-all duration-500 ease-in-out group-hover:scale-105'
                  />
                </div>
                <div className='absolute bottom-0 left-0 flex h-1/2 w-full flex-col justify-end bg-gradient-to-t from-neutral-800 to-transparent p-4 pb-3 text-white md:pb-6'>
                  <h3 className='text-lg font-medium uppercase text-white 2xl:text-2xl'>
                    {outfitHighlight.title}
                  </h3>
                  <p className='text-xs text-white opacity-90 md:text-base'>
                    {outfitHighlight.description}
                  </p>
                </div>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>

        <CarouselNavigation />
      </Carousel>
    </CollectionContainer>
  )
}

function CarouselNavigation() {
  return (
    <>
      <CarouselPrevious
        className='absolute -left-3 top-1/2 h-auto w-auto -translate-y-1/2 rounded-full bg-black p-2 text-white md:-left-5 md:p-3'
        variant='outline'
        hideIfStart
      />
      <CarouselNext
        className='absolute -right-3 top-1/2 h-auto w-auto -translate-y-1/2 rounded-full bg-black p-2 text-white md:-right-5 md:p-3'
        variant='outline'
        hideIfEnd
      />
    </>
  )
}
